import "./Dashboard.css";
import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { useNavigate } from "react-router";
import Groups2Icon from "@mui/icons-material/Groups2";
import HolidayVillageIcon from "@mui/icons-material/HolidayVillage";
import SchoolIcon from "@mui/icons-material/School";
import ClassIcon from "@mui/icons-material/Class";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { MoonLoader } from "react-spinners";
import AddIcon from "@mui/icons-material/Add";
import CourseCard from "../../Components/Molecules/Cards/CourseCard/CourseCard";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Dashboard({
  contextData,
  statistics,
  loading,
  classes,
  classesLoading,
}) {
  const navigate = useNavigate();
  const date = new Date();

  if (loading) {
    return (
      <div className="mainContainer">
        <div className="loadingContainer">
          <MoonLoader color="#012369" size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="mainContainer">
      {/* Welcome Header */}
      <div className="welcomeHeaderDashboard">
        <div className="welcomeContent">
          <div className="welcomeTitleDashboard">
            <div className="titleIcon">
              <DashboardIcon />
            </div>
            <div className="titleTextDashboard">
              <h1>
                Welcome to{" "}
                {contextData?.role === "admin"
                  ? "Skill Code"
                  : contextData?.user}{" "}
                Dashboard
              </h1>
              <p>
                School Year {date.getYear() + 1900} -{" "}
                {date.getYear() + 1900 + 1}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="statsGrid">
        {contextData?.role === "admin" && (
          <div className="statCard" onClick={() => navigate("/schools")}>
            <div className="statIcon primary">
              <HolidayVillageIcon />
            </div>
            <div className="statContent">
              <h4>Schools</h4>
              <h2>{statistics?.counting?.totalSchools || 0}</h2>
            </div>
          </div>
        )}

        <div className="statCard" onClick={() => navigate("/instructors")}>
          <div className="statIcon success">
            <SchoolIcon />
          </div>
          <div className="statContent">
            <h4>Instructors</h4>
            <h2>{statistics?.counting?.totalTeachers || 0}</h2>
          </div>
        </div>

        <div className="statCard" onClick={() => navigate("/students")}>
          <div className="statIcon warning">
            <Groups2Icon />
          </div>
          <div className="statContent">
            <h4>Students</h4>
            <h2>{statistics?.counting?.totalStudents || 0}</h2>
          </div>
        </div>

        <div className="statCard" onClick={() => navigate("/classes")}>
          <div className="statIcon info">
            <ClassIcon />
          </div>
          <div className="statContent">
            <h4>Classes</h4>
            <h2>{statistics?.counting?.totalClasses || 0}</h2>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="chartsSection">
        <div className="chartGrid">
          {/* Student Enrollment Chart */}
          <div className="chartCard large">
            <div className="chartHeader">
              <div className="chartTitle">
                <BarChartIcon className="chartIcon" />
                <h3>Student Enrollment by Month</h3>
              </div>
            </div>
            <div className="chartContent">
              {statistics?.studentsEnrollmentOverYear?.length ? (
                <Line
                  data={{
                    labels:
                      statistics?.studentsEnrollmentOverYear?.map(
                        (item) => item?.month
                      ) || [],
                    datasets: [
                      {
                        label: "Students Enrolled",
                        data:
                          statistics?.studentsEnrollmentOverYear?.map(
                            (item) => item?.enrollments
                          ) || [],
                        borderColor: "#012369",
                        backgroundColor: "rgba(1, 35, 105, 0.1)",
                        borderWidth: 5,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#012369",
                        pointBorderColor: "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "top",
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                          font: {
                            size: 12,
                            weight: "600",
                          },
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        titleColor: "#333",
                        bodyColor: "#666",
                        borderColor: "#e0e0e0",
                        borderWidth: 1,
                        cornerRadius: 8,
                        displayColors: true,
                        titleFont: {
                          size: 14,
                          weight: "bold",
                        },
                        bodyFont: {
                          size: 13,
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                        },
                        ticks: {
                          color: "#666",
                          font: {
                            size: 11,
                          },
                        },
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: "rgba(0, 0, 0, 0.05)",
                        },
                        ticks: {
                          color: "#666",
                          font: {
                            size: 11,
                          },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <div className="emptyChart">
                  <BarChartIcon className="emptyIcon" />
                  <p>No enrollment data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Classes per Course Pie Chart */}
          <div className="chartCard small">
            <div className="chartHeader">
              <div className="chartTitle">
                <PieChartIcon className="chartIcon" />
                <h3>Classes per Program</h3>
              </div>
            </div>
            <div className="chartContent">
              {statistics?.numberOfClassesPerCourse?.length ? (
                <Doughnut
                  data={{
                    labels:
                      statistics?.numberOfClassesPerCourse
                        ?.slice(0, 5)
                        ?.map((item) => item?.courseTitle) || [],
                    datasets: [
                      {
                        data:
                          statistics?.numberOfClassesPerCourse
                            ?.slice(0, 5)
                            ?.map((item) => item?.totalClasses) || [],
                        backgroundColor: [
                          "#012369",
                          "#1976d2",
                          "#42a5f5",
                          "#90caf9",
                          "#bbdefb",
                        ],
                        borderColor: "#ffffff",
                        borderWidth: 2,
                        hoverBorderWidth: 3,
                        hoverOffset: 8,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "60%",
                    plugins: {
                      legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          padding: 15,
                          font: {
                            size: 11,
                            weight: "500",
                          },
                          boxWidth: 12,
                          boxHeight: 12,
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        titleColor: "#333",
                        bodyColor: "#666",
                        borderColor: "#e0e0e0",
                        borderWidth: 1,
                        cornerRadius: 8,
                        displayColors: true,
                        titleFont: {
                          size: 14,
                          weight: "bold",
                        },
                        bodyFont: {
                          size: 13,
                        },
                        callbacks: {
                          label: function (context) {
                            return `${context.label}: ${context.parsed} classes`;
                          },
                        },
                      },
                    },
                    elements: {
                      arc: {
                        borderWidth: 0,
                      },
                    },
                  }}
                />
              ) : (
                <div className="emptyChart">
                  <PieChartIcon className="emptyIcon" />
                  <p>No program data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Gender Distribution Chart */}
          <div className="chartCard medium">
            <div className="chartHeader">
              <div className="chartTitle">
                <PersonIcon className="chartIcon" />
                <h3>Gender Distribution by Age</h3>
              </div>
            </div>
            <div className="chartContent">
              {statistics?.agesWithGender?.length ? (
                <Bar
                  data={{
                    labels:
                      statistics?.agesWithGender?.map((item) => item?.age) ||
                      [],
                    datasets: [
                      {
                        label: "Male",
                        data:
                          statistics?.agesWithGender?.map(
                            (item) => item?.male
                          ) || [],
                        backgroundColor: "#1976d2",
                        borderColor: "#1565c0",
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                      },
                      {
                        label: "Female",
                        data:
                          statistics?.agesWithGender?.map(
                            (item) => item?.female
                          ) || [],
                        backgroundColor: "#dc004e",
                        borderColor: "#c51162",
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "top",
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                          font: {
                            size: 12,
                            weight: "600",
                          },
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        titleColor: "#333",
                        bodyColor: "#666",
                        borderColor: "#e0e0e0",
                        borderWidth: 1,
                        cornerRadius: 8,
                        displayColors: true,
                        titleFont: {
                          size: 14,
                          weight: "bold",
                        },
                        bodyFont: {
                          size: 13,
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                        },
                        ticks: {
                          color: "#666",
                          font: {
                            size: 11,
                          },
                        },
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: "rgba(0, 0, 0, 0.05)",
                        },
                        ticks: {
                          color: "#666",
                          font: {
                            size: 11,
                          },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <div className="emptyChart">
                  <PersonIcon className="emptyIcon" />
                  <p>No gender data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Average Grades Chart */}
          <div className="chartCard medium">
            <div className="chartHeader">
              <div className="chartTitle">
                <TrendingUpIcon className="chartIcon" />
                <h3>Average Grades per Program</h3>
              </div>
            </div>
            <div className="chartContent">
              {statistics?.coursesWithAverageGrade?.length ? (
                <Bar
                  data={{
                    labels:
                      statistics?.coursesWithAverageGrade?.map(
                        (item) => item?.courseTitle
                      ) || [],
                    datasets: [
                      {
                        label: "Average Grade",
                        data:
                          statistics?.coursesWithAverageGrade?.map(
                            (item) => item?.averageGrade
                          ) || [],
                        backgroundColor: "#012369",
                        borderColor: "#012369",
                        borderWidth: 2,
                        borderRadius: 6,
                        borderSkipped: false,
                        gradient: {
                          backgroundColor: {
                            type: "linear",
                            x0: 0,
                            y0: 0,
                            x1: 0,
                            y1: 1,
                            colorStops: [
                              { offset: 0, color: "#012369" },
                              { offset: 1, color: "#42a5f5" },
                            ],
                          },
                        },
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "top",
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                          font: {
                            size: 12,
                            weight: "600",
                          },
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        titleColor: "#333",
                        bodyColor: "#666",
                        borderColor: "#e0e0e0",
                        borderWidth: 1,
                        cornerRadius: 8,
                        displayColors: true,
                        titleFont: {
                          size: 14,
                          weight: "bold",
                        },
                        bodyFont: {
                          size: 13,
                        },
                        callbacks: {
                          label: function (context) {
                            return `Average Grade: ${context.parsed.y}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                        },
                        ticks: {
                          color: "#666",
                          font: {
                            size: 10,
                          },
                          maxRotation: 45,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        max: 5,
                        grid: {
                          color: "rgba(0, 0, 0, 0.05)",
                        },
                        ticks: {
                          color: "#666",
                          font: {
                            size: 11,
                          },
                          callback: function (value) {
                            return value;
                          },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <div className="emptyChart">
                  <TrendingUpIcon className="emptyIcon" />
                  <p>No grade data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Latest Classes Section */}
      <div className="contentSection">
        <div className="classesHeader">
          <h2 className="sectionTitle" style={{ borderBottom: "none" }}>
            Latest Classes
          </h2>
          <button
            className="actionButton primary"
            onClick={() => navigate("/classes")}
          >
            See All Classes
          </button>
        </div>

        {classesLoading ? (
          <div className="loadingContainer">
            <MoonLoader color="#012369" size={40} />
          </div>
        ) : classes?.length ? (
          <div className="classesGrid">
            {classes?.map((item) => (
              <CourseCard
                class_id={item?.id}
                class_name={item?.className}
                course={item?.course?.title}
                teacher={item?.teacher?.name}
                school={item?.school?.name}
                terminated={item?.isTerminated}
              />
            ))}
          </div>
        ) : (
          <div className="emptyState">
            <div className="emptyIcon">
              <ClassIcon />
            </div>
            <h3>No Classes Found</h3>
            <p>
              No classes have been created yet. Start by adding your first
              class.
            </p>
            <button
              className="actionButton primary"
              onClick={() => navigate("/classes/add")}
              style={{ margin: "20px auto" }}
            >
              <AddIcon />
              Add First Class
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
